'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { NavItem } from './NavItem'
import { DashboardNavGroup, isNavItemActive } from './navigation'

/**
 * Landlord's width, and the floor for the kit. Four short labels fit it; a
 * dashboard with longer ones ("Bills & Payments", "Account Settings") or group
 * headings should pass more rather than let its labels wrap or truncate.
 */
export const DEFAULT_SIDEBAR_WIDTH = 210

export interface SidebarLogo {
  src: string
  alt: string
  width: number
  height: number
}

export interface SidebarProps {
  navGroups: DashboardNavGroup[]
  logo: SidebarLogo
  homeHref: string
  /** Avatar block under the logo. */
  profile?: React.ReactNode
  /** Support / account-manager card pinned to the bottom. */
  footer?: React.ReactNode
  /** Must match the AppShell's navbar width, which reserves the space. */
  width?: number
}

/**
 * The desktop sidebar.
 *
 * Grouped rather than flat. Landlord has four destinations and a flat list was
 * fine; co-owner has eleven, and eleven undifferentiated rows is a list you read
 * rather than a menu you scan. A group with no `label` renders with no heading,
 * so landlord's single unlabelled group looks exactly as it did.
 *
 * The panel scrolls: eleven items plus headings, the profile block and the
 * support card exceed a 900px-tall viewport.
 */
export const Sidebar: React.FC<SidebarProps> = ({
  navGroups,
  logo,
  homeHref,
  profile,
  footer,
  width = DEFAULT_SIDEBAR_WIDTH,
}) => {
  const pathname = usePathname()

  return (
    // start-0 rather than left-0 so the panel follows the writing direction.
    // Width is inline because it has to agree with the AppShell's navbar width,
    // which is a number rather than a class.
    <div
      style={{ width }}
      className="h-screen fixed start-0 top-0 bg-white/90 outline outline-1 outline-offset-[-0.50px] outline-zinc-200 overflow-hidden"
    >
      {/*
        The height budget, for whoever changes these numbers next. Co-owner is
        the worst case: logo ~63 + profile ~94 + eleven 33px rows with 4px gaps
        ~403 + support card ~105 + sign-out ~40, plus the padding here. That is
        around 770px, so it fits a 1080p window and scrolls on a 1366x768 laptop
        — which is what overflow-y-auto is for. Adding another 4px per row costs
        ~50px; take it out of the support card or the profile block, not the rows.
      */}
      <div className="w-full h-full pb-4 flex flex-col justify-between overflow-y-auto">
        <div className="flex flex-col gap-4 flex-shrink-0">
          <div className="flex flex-col gap-2">
            <div className="px-7 pt-4 pb-1 flex flex-col justify-center items-center gap-2 overflow-hidden">
              <Link href={homeHref} className="dashboard-nav-link flex items-center justify-center">
                <Image
                  className="h-auto object-contain"
                  style={{ width: logo.width }}
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  unoptimized
                />
              </Link>
            </div>
            {profile && <div className="px-4 flex flex-col items-center gap-3">{profile}</div>}
          </div>

          {/* gap-3 between groups rather than gap-5: the headings already
              separate them, so the extra 8px per gap was only buying height.
              gap-1 within a group — at gap-0.5 the rounded rows very nearly
              touched, which is what made the list look crowded. */}
          <nav className="px-3.5 flex flex-col gap-3">
            {navGroups.map((group) => (
              <div key={group.id} className="flex flex-col gap-1">
                {group.label && (
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-dashboard-axis">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => (
                  <NavItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    badge={item.badge}
                    external={item.external}
                    active={isNavItemActive(pathname, item)}
                  />
                ))}
              </div>
            ))}
          </nav>
        </div>

        {/* pt-4 so the footer never crowds the last nav row when the panel is
            tall enough not to scroll. */}
        {footer && <div className="px-3.5 pt-4 relative flex flex-col gap-2.5">{footer}</div>}
      </div>
    </div>
  )
}
